import React, { useMemo } from 'react';
import { Label, Button, Icon } from 'components';
import { Menu, MenuItem } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import { useModal } from 'providers/modalProvider';
import { RenderIf } from 'config';
import { isEmpty } from 'ramda';
import { title } from 'utils/helpers';
import LogoIcon from 'assets/logo.png';
import PropTypes from 'prop-types';
import { USER_LINE_NAMES } from 'utils/enums';

const ViewAsOfflineMenuItem = ({ includeIcon, initImpersonation }) => (
  <MenuItem
    text="View as Offline soccer"
    icon={includeIcon && <Icon customIcon="impersonation" />}
    onClick={() =>
      initImpersonation({
        type: 'ENTER_IMPERSONATION',
        payload: USER_LINE_NAMES.OFFLINE,
      })
    }
  />
);

const ViewAsVideoMenuItem = ({ includeIcon, initImpersonation }) => (
  <MenuItem
    text="View as Video"
    icon={includeIcon && <Icon customIcon="impersonation" />}
    onClick={() =>
      initImpersonation({
        type: 'ENTER_IMPERSONATION',
        payload: USER_LINE_NAMES.VIDEO,
      })
    }
  />
);

const Header = ({
  user = {},
  loginWithRedirect,
  isAuthenticated,
  authLoading,
  sideCollapsed,
  setSideCollapsed,
}) => {
  const { modalDispatch } = useModal();
  const { firstName = '', lastName = '', jobTitleRole } = user;
  const userLineName = jobTitleRole?.role?.line?.name;
  const roleName = jobTitleRole?.role?.name;
  const isSuperAdmin = roleName === 'super-admin';
  const initImpersonation = ({ type, payload }) => {
    modalDispatch({
      type,
      payload,
    });
  };

  const quickActions = useMemo(
    () => (
      <Menu className="header-sign-out-menu">
        <MenuItem
          text="Change Password"
          icon={<Icon customIcon="password" />}
          onClick={() =>
            modalDispatch({
              type: 'CHANGE_PASSWORD',
            })
          }
        />
        <RenderIf condition={userLineName === USER_LINE_NAMES.OFFLINE}>
          <ViewAsVideoMenuItem
            includeIcon
            initImpersonation={initImpersonation}
          />
        </RenderIf>
        <RenderIf condition={userLineName === USER_LINE_NAMES.VIDEO}>
          <ViewAsOfflineMenuItem
            includeIcon
            initImpersonation={initImpersonation}
          />
        </RenderIf>
        <RenderIf condition={isSuperAdmin}>
          <MenuItem text="View as " icon={<Icon customIcon="impersonation" />}>
            <ViewAsOfflineMenuItem initImpersonation={initImpersonation} />
            <ViewAsVideoMenuItem initImpersonation={initImpersonation} />
          </MenuItem>
        </RenderIf>
        <MenuItem
          text="Sign Out"
          icon={<Icon customIcon="out" />}
          onClick={() =>
            modalDispatch({
              type: 'SIGN_OUT',
            })
          }
        />
      </Menu>
    ),
    [user]
  );
  return (
    <div className="header-container">
      <div className="logo-container">
        <img src={LogoIcon} alt="Logo" />
      </div>
      <div className="flex items-center">
        <RenderIf
          condition={isAuthenticated}
          renderElse={
            <Button
              onClick={loginWithRedirect}
              intent="Primary"
              loading={authLoading}
            >
              Sign In
            </Button>
          }
        >
          <RenderIf condition={!isEmpty(user)}>
            <Popover2 content={quickActions} position="bottom">
              <Label
                variant="AvatarLabel"
                labelText={`${title(firstName)} ${title(lastName)}`}
                avatarText={`${firstName} ${lastName}`}
                className="automarginLeft"
                size={20}
              />
            </Popover2>
          </RenderIf>
          <Button
            minimal
            icon="menu"
            className="header-expand-side"
            onClick={() => setSideCollapsed(!sideCollapsed)}
          />
        </RenderIf>
      </div>
    </div>
  );
};

export default Header;
Header.propTypes = {
  user: PropTypes.shape({}).isRequired,
  loginWithRedirect: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  authLoading: PropTypes.bool.isRequired,
  sideCollapsed: PropTypes.bool.isRequired,
  setSideCollapsed: PropTypes.func.isRequired,
};
